import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { looseListT } from '@/i18n/looseT';

import { useDocumentTitle } from './useDocumentTitle';

import './publicSite.css';

/**
 * `/` — HAi Creative 官網首頁(公開頁,不包 `RequireAuth`)
 * -----------------------------------------------------------------------------
 * 版面與文案逐塊來自文案稿 artifact `94d6493f`,**文字不改寫**;要動文案回那份取。
 *
 * ⚠️ **為什麼是 `/` 而不是 `/home`**:`/home` 已經是登入後的儀表板。owner 2026-09-11
 * 拍板官網放根路徑,並在頁上多一顆「立即啟用」直接進 `/home` —— **刻意不做自動轉址**,
 * 已登入的人進 `/` 也是看到官網,自己按按鈕進去。(mentor 是把官網放在另一個網域
 * `www.mentor4ads.com`,我們沒有那個網域,所以收在同一個 SPA 的根路徑。)
 *
 * ⚠️ **「立即啟用」是文案稿裡沒有的新增內容**(owner 指名要加的那顆);其餘一字不動。
 *
 * ⚠️ **語言選單刻意不畫**(owner:「前端那邊選語言先關掉就好」)—— `site.json` 的
 * ja / en 是未審校初稿。原稿那顆本來也是 `href="#"` 的假選單。
 *
 * ⚠️ **原稿的「待確認」黃標已移除**(草稿說明本來就寫「實作時移除」),但**被標記的
 * 內容仍是未確認的**:對外主打版位清單、公司介紹說法、地址、客服信箱。上線前要 owner
 * 逐項確認 —— 地址與信箱是會被公開的真實資訊。
 */
export function LandingPage() {
  const { t } = useTranslation('site');
  // 清單型文案(功能卡 / 痛點 / FAQ …)走共用的 `looseListT`,見 `i18n/looseT.ts`
  const list = looseListT(t);
  useDocumentTitle(t('landing.docTitle'));

  return (
    <div className="hai-site hai-site--landing" data-testid="landing-page">
      <header className="nav">
        <div className="wrap nav-in">
          <a className="brand" href="#top" aria-label={t('brand')}>
            <span className="mark">H</span>
            <span>
              H<span className="ai">Ai</span>&nbsp;Creative
            </span>
          </a>
          <nav className="nav-links" aria-label={t('landing.nav.solutions')}>
            <a href="#solutions">{t('landing.nav.solutions')}</a>
            <a href="#features">{t('landing.nav.features')}</a>
            <a href="#pricing">{t('landing.nav.pricing')}</a>
            <a href="#faq">{t('landing.nav.faq')}</a>
          </nav>
          <div className="nav-cta">
            <Link className="link-quiet" to="/login">
              {t('landing.nav.login')}
            </Link>
            {/* owner 指名新增:直接進工作台(未登入會被 RequireAuth 導去 /login) */}
            <Link className="btn btn-primary" to="/home" data-testid="landing-start">
              {t('landing.nav.start')}
            </Link>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ============ HERO ============ */}
        <section className="hero" id="hero">
          <div className="wrap hero-grid">
            <div>
              <span className="hero-kicker">{t('landing.hero.kicker')}</span>
              <h1>
                {t('landing.hero.titleLine1')}
                <br />
                {t('landing.hero.titleLine2')}
                <br />
                <span className="brand-line">{t('landing.hero.titleBrand')}</span>
              </h1>
              <p className="hero-sub">
                <Trans t={t} i18nKey="landing.hero.sub" components={{ b: <b /> }} />
              </p>
              <div className="hero-ctas">
                <a className="btn btn-primary btn-lg" href="#pricing">
                  {t('landing.hero.cta')}
                  <ArrowIcon />
                </a>
              </div>
              <div className="hero-assure">
                {list('landing.hero.assurances').map((x) => (
                  <span key={x}>
                    <CheckCircleIcon />
                    {x}
                  </span>
                ))}
              </div>
            </div>

            {/* 產品畫布示意:輸入 → 綠 TOOL 卡 → 藍 OUTPUT 卡 */}
            <div className="canvas-frame" role="img" aria-label={t('landing.hero.canvasAlt')}>
              <div className="canvas-chrome">
                <i />
                <i />
                <i />
                <span>{t('landing.hero.canvasChrome')}</span>
              </div>
              <div className="canvas-stage">
                <svg className="wires" viewBox="0 0 560 470" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M162 205 C 215 205, 145 78, 197 74" />
                  <path d="M162 225 C 222 232, 150 308, 197 306" />
                  <path d="M369 71 C 392 71, 392 88, 412 90" />
                  <path d="M369 305 C 390 305, 392 300, 412 300" />
                </svg>
                <div className="ncard c-input">
                  <span className="npill tool">
                    <i />
                    {t('landing.hero.nodeInput')}
                  </span>
                  <div className="nthumb photo" style={{ height: 64 }} />
                  <div className="nrow w60" />
                </div>
                <div className="ncard c-img">
                  <span className="npill tool">
                    <i />
                    {t('landing.hero.nodeImage')}
                  </span>
                  <div className="nrow" />
                  <div className="nrow w60" />
                  <span className="ngen">✦ {t('landing.hero.nodeGenerate')}</span>
                </div>
                <div className="ncard c-vid">
                  <span className="npill tool">
                    <i />
                    {t('landing.hero.nodeVideo')}
                  </span>
                  <div className="nrow" />
                  <div className="nrow w60" />
                  <span className="ngen">✦ {t('landing.hero.nodeGenerate')}</span>
                </div>
                <div className="ncard c-out1">
                  <span className="npill out">
                    <i />
                    {t('landing.hero.nodeOut1')}
                  </span>
                  <div className="nthumb" style={{ aspectRatio: '16/10' }} />
                </div>
                <div className="ncard c-out2">
                  <span className="npill out">
                    <i />
                    {t('landing.hero.nodeOut2')}
                  </span>
                  <div className="nthumb" />
                </div>
                <span className="canvas-cap">{t('landing.hero.canvasCaption')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 版位 strip ============ */}
        <div className="strip">
          <div className="wrap strip-in">
            <span className="strip-label">{t('landing.strip.label')}</span>
            {list('landing.strip.items').map((x) => (
              <b key={x}>{x}</b>
            ))}
          </div>
        </div>

        {/* ============ 痛點 ============ */}
        <section className="pain" id="solutions">
          <div className="wrap">
            <div className="pain-head">
              <span className="eyebrow">{t('landing.pain.eyebrow')}</span>
              <h2>
                {t('landing.pain.titleLine1')}
                <br />
                {t('landing.pain.titleLine2')}
              </h2>
              <p className="sec-lead">{t('landing.pain.lead')}</p>
            </div>
            <div className="pain-grid">
              {list<{ who: string; title: string; items: string[] }>('landing.pain.cards').map((card) => (
                <article className="pain-card" key={card.who}>
                  <div className="who">{card.who}</div>
                  <h3>{card.title}</h3>
                  <ul>
                    {card.items.map((x) => (
                      <li key={x}>
                        <CrossIcon />
                        {x}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 定位 ============ */}
        <section className="position">
          <div className="wrap">
            <span className="eyebrow">{t('landing.position.eyebrow')}</span>
            <h2>
              {t('landing.position.titleLine1')}
              <br />
              {t('landing.position.titleLine2')}
            </h2>
            <p>{t('landing.position.para1')}</p>
            <p>{t('landing.position.para2')}</p>
            <a className="btn btn-primary btn-lg" href="#pricing">
              {t('landing.position.cta')}
            </a>
          </div>
        </section>

        {/* ============ 功能 ============ */}
        <section className="features" id="features">
          <div className="wrap">
            <div className="feat-head">
              <span className="eyebrow">{t('landing.features.eyebrow')}</span>
              <h2>{t('landing.features.title')}</h2>
              <p className="sec-lead">{t('landing.features.lead')}</p>
            </div>
            <div className="feat-grid">
              {list<{ title: string; desc: string; items: string[] }>('landing.features.cards').map(
                (card, i) => (
                  <article className="feat-card" key={card.title}>
                    <div className="feat-icon">{FEATURE_ICONS[i]}</div>
                    <h3>{card.title}</h3>
                    <p>{card.desc}</p>
                    <ul>
                      {card.items.map((x) => (
                        <li key={x}>
                          <CheckIcon />
                          {x}
                        </li>
                      ))}
                    </ul>
                  </article>
                ),
              )}
            </div>
          </div>
        </section>

        {/* ============ 比較 ============ */}
        <section id="compare">
          <div className="wrap">
            <div className="compare-head">
              <span className="eyebrow">{t('landing.compare.eyebrow')}</span>
              <h2>{t('landing.compare.title')}</h2>
              <p className="sec-lead">{t('landing.compare.lead')}</p>
            </div>
            <div className="cmp-grid">
              <div className="cmp-card hero-side">
                <h3>
                  {t('landing.compare.nowTitle')}
                  <span className="cmp-tag">{t('landing.compare.nowTag')}</span>
                </h3>
                <ul>
                  {list('landing.compare.nowItems').map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
              <div className="cmp-card plain">
                <h3>
                  {t('landing.compare.beforeTitle')}
                  <span className="cmp-tag">{t('landing.compare.beforeTag')}</span>
                </h3>
                <ul>
                  {list('landing.compare.beforeItems').map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="stats">
              {list<{ num: string; label: string }>('landing.compare.stats').map((s) => (
                <div className="stat" key={s.label}>
                  <div className="num">{s.num}</div>
                  <div className="lbl">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 價格 ============ */}
        <section className="pricing" id="pricing">
          <div className="wrap">
            <div className="price-head">
              <span className="eyebrow">{t('landing.pricing.eyebrow')}</span>
              <h2>{t('landing.pricing.title')}</h2>
              <p className="sec-lead">{t('landing.pricing.lead')}</p>
            </div>
            <p className="price-note">{t('landing.pricing.note')}</p>
            <div className="price-grid">
              {list<{
                title: string;
                for: string;
                price: string;
                priceNote: string;
                items: string[];
                cta: string;
              }>('landing.pricing.cards').map((card, i) => {
                const featured = i === 0;
                return (
                  <div className={featured ? 'price-card featured' : 'price-card'} key={card.title}>
                    <h3>{card.title}</h3>
                    <div className="for">{card.for}</div>
                    <div className="price-line">
                      {card.price}
                      <small>{card.priceNote}</small>
                    </div>
                    <ul>
                      {card.items.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                    {featured ? (
                      <Link className="btn btn-primary" to="/home">
                        {card.cta}
                      </Link>
                    ) : (
                      <a className="btn btn-ghost" href="#footer-contact">
                        {card.cta}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="faq" id="faq">
          <div className="wrap">
            <div className="faq-head">
              <span className="eyebrow">{t('landing.faq.eyebrow')}</span>
              <h2>{t('landing.faq.title')}</h2>
            </div>
            {list<{ q: string; a: string }>('landing.faq.items').map((item, i) => (
              <details key={item.q} open={i === 0}>
                <summary>
                  <span className="q-dot" />
                  {item.q}
                  <ChevronIcon />
                </summary>
                <div className="faq-a">{item.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* ============ 底部 CTA ============ */}
        <section className="final" id="cta">
          <div className="wrap">
            <h2>{t('landing.final.title')}</h2>
            <p>{t('landing.final.lead')}</p>
            <a className="btn btn-primary btn-lg" href="#pricing">
              {t('landing.final.cta')}
            </a>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-brand">
              <a className="brand" href="#top">
                <span className="mark">H</span>
                <span>
                  H<span className="ai">Ai</span>&nbsp;Creative
                </span>
              </a>
              <p>{t('landing.footer.brandDesc')}</p>
            </div>
            <div>
              <h3>{t('landing.footer.productTitle')}</h3>
              <ul>
                {list('landing.footer.productItems').map((x) => (
                  <li key={x}>
                    <a href="#features">{x}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>{t('landing.footer.supportTitle')}</h3>
              <ul>
                <li>
                  <a href="#faq">{t('landing.footer.supportFaq')}</a>
                </li>
                <li>
                  <a href="#footer-contact">{t('landing.footer.supportContact')}</a>
                </li>
                <li>
                  <Link to="/user-terms">{t('docFooter.terms')}</Link>
                </li>
                <li>
                  <Link to="/disclaimer">{t('docFooter.disclaimer')}</Link>
                </li>
              </ul>
            </div>
            <div id="footer-contact">
              <h3>{t('landing.footer.contactTitle')}</h3>
              <ul className="foot-contact">
                <li>
                  <span>{t('landing.footer.address')}</span>
                </li>
                <li>
                  <span>{t('landing.footer.email')}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="foot-bottom">
            <span>{t('docFooter.copyright')}</span>
            <span>
              <Link to="/user-terms">{t('docFooter.terms')}</Link>
              &nbsp;&nbsp;·&nbsp;&nbsp;
              <Link to="/disclaimer">{t('docFooter.disclaimer')}</Link>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── 圖示(逐個照抄文案稿的 inline SVG;純裝飾一律 aria-hidden)──────────────

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h9m0 0L8.5 4.5M12 8l-3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 8.2l2 2L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3.5 3.5l7 7m0-7l-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="chev" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6.5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** 六張功能卡的圖示,順序對齊 `site.json` 的 `landing.features.cards`。 */
const FEATURE_ICONS = [
  <svg key="flow" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="2" y="3" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <rect x="13" y="13" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 6h3a2 2 0 012 2v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>,
  <svg key="image" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.5" cy="8.5" r="1.8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4 16l4.5-4.5L12 15l2.5-2.5L19 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg key="video" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="2.5" y="4.5" width="17" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9.5 8.5l4.5 2.5-4.5 2.5v-5z" fill="currentColor" />
  </svg>,
  <svg key="copy" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M4 5h14M4 9.5h14M4 14h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M15.5 13l1 2.2 2.2 1-2.2 1-1 2.2-1-2.2-2.2-1 2.2-1 1-2.2z" fill="currentColor" />
  </svg>,
  <svg key="library" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M11 3l7 3.5v5c0 4-3 6.5-7 7.5-4-1-7-3.5-7-7.5v-5L11 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 11l2 2 4-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg key="ads" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M3.5 18.5V13m5 5.5V9m5 9.5V11m5 7.5V5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>,
];
